import { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import StepGuide from "../components/StepGuide";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { checkPermission, showKAlert } from "../helpers/helpers";
import { t } from "../locale";
import VerificationStepNav from "../components/VerificationStepNav";
import { fetchDocumentImage, getQRCodeForUrl } from "../service/image";
import QRCodeModal from "../components/QRCodeModal";
import QrCodeHelpButton from "./QrCodeHelpButton";

type PoseKey = 'forward' | 'left' | 'down';
type LivenessStep = 'ready' | 'forward' | 'left' | 'down' | 'done' | '';

function FaceIdentification() {
  const navigate = useNavigate();

  const documentImageData = useRef<string>('')

  const [livenessStep, setLivenessStep] = useState<LivenessStep>('ready');
  const [message, setMessage] = useState('');
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [similarity, setSimilarity] = useState<number | null>(null);
  const [livenesspassed, setLivenessPassed] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [spaceHeight, setSpaceHeight] = useState('mt-5')

  const [mode, setMode] = useState<'camera' | 'upload'>('camera');

  const [uploads, setUploads] = useState<Partial<Record<PoseKey, string>>>({});
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<PoseKey, string>>>({});

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const pathSegments = window.location.pathname.split("/");
  const companyId = pathSegments[1];
  const requestId = pathSegments[2];

  const { mobileNav, desktopNav } = VerificationStepNav({
    currentStep: 3,
    companyId,
    requestId,
    onBack: undefined,
    spaceHeight
  })

  const [showModal, setShowModal] = useState(false);
  const [qrCodeSrc, setQrCodeSrc] = useState('');

  useEffect(() => {
    checkPermission();

    getQRCodeForUrl(`${process.env.REACT_APP_URL}/${companyId}/${requestId}/face_identify`)
      .then(res => setQrCodeSrc(res.qrCode));

    if (isMobile) {
      setSpaceHeight('')
    }
  }, [livenessStep, mode]);

  useEffect(() => {
    return () => stopVideo();
  }, []);

  useEffect(() => {
    if (mode === 'upload') {
      stopVideo();
      setVideoLoaded(false);
      setLivenessStep('ready');
    } else {
      setUploads({});
      setUploadErrors({});
    }
  }, [mode]);

  const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream as MediaStream;
        await videoRef.current.play();
        setVideoLoaded(true);
      }
    } catch (err) {
      console.error('Cannot access camera:', err);
      setMessage(t('cannotAccessCamera'));
    }
  };

  const stopVideo = () => {
    const v = videoRef.current;
    const ms = v?.srcObject as MediaStream | null;
    ms?.getTracks().forEach(tr => tr.stop());
    if (v) v.srcObject = null;
  };

  const captureFrame = (): string | null => {
    if (!videoRef.current) return null;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');

    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
  };

  const isHeic = (file: File): boolean =>
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif');

  const fileToBase64Png = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/') && file.type !== '') {
        reject(new Error('Invalid file type'));
        return;
      }
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('File read error'));
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const W = 720, H = 960;
          const canvas = document.createElement('canvas');
          canvas.width = W; canvas.height = H;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas not supported')); return; }

          const scale = Math.min(W / img.width, H / img.height);
          const drawW = Math.round(img.width * scale);
          const drawH = Math.round(img.height * scale);
          const dx = Math.floor((W - drawW) / 2);
          const dy = Math.floor((H - drawH) / 2);

          ctx.clearRect(0, 0, W, H);
          ctx.drawImage(img, dx, dy, drawW, drawH);

          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl.replace(/^data:image\/png;base64,/, ''));
        };
        img.onerror = () => reject(new Error('Image load error'));
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });


  const convertHeicToFile = async (file: File): Promise<File> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/convert-image`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error('Konvertierung fehlgeschlagen');
    const { base64 } = await res.json() as { base64: string };
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], file.name.replace(/\.(heic|heif)$/i, '.png'), { type: 'image/png' });
  };

  const handleUpload = async (pose: PoseKey, file?: File | null) => {
    setUploadErrors(prev => ({ ...prev, [pose]: '' }));
    if (!file) return;

    if (!file.type.startsWith('image/') && !isHeic(file)) {
      setUploadErrors(prev => ({ ...prev, [pose]: 'Nedozvoljen tip fajla' }));
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setUploadErrors(prev => ({ ...prev, [pose]: 'Maksimalno 6MB' }));
      return;
    }
    try {
      const fileToProcess = isHeic(file) ? await convertHeicToFile(file) : file;
      const b64 = await fileToBase64Png(fileToProcess);
      setUploads(prev => ({ ...prev, [pose]: b64 }));
    } catch (e: unknown) {
      console.error(e);
      setUploadErrors(prev => ({ ...prev, [pose]: 'Greška pri obradi slike' }));
    }
  };

  const sendForVerification = async (poses: { forward: string | null, left: string | null, down: string | null }) => {
    setIsProcessing(true);
    setMessage(t('Analyzing') + '...');
    const fromServer = await fetchDocumentImage(requestId);
    documentImageData.current = fromServer ?? localStorage.getItem('documentImage') ?? '';

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/verify/liveness`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId, requestId, documentImage: documentImageData.current, poses })
    });

    const result = await response.json();
    setIsProcessing(false);

    const pct = Math.round((result.similarity ?? 0) * 100)
    setSimilarity(pct)
    setShowResultModal(true)

    if (result.status === true) {
      setLivenessPassed(true)
      setMessage(t('livnessSuccess'));
    } else {
      setLivenessPassed(false)
      setMessage(result.message || t('livnessNotSuccess'));
    }
  };

  const startLivenessProcess = async () => {
    if (mode === 'upload') {
      return submitUploads();
    }

    setIsProcessing(false);
    await startVideo();
    await wait(1200);
    setLivenessStep('forward');
    setMessage(t('watchForward'));
    await wait(3800);
    const forward = captureFrame();

    setLivenessStep('left');
    setMessage(t('watchLeft'));
    await wait(3800);
    const left = captureFrame();

    setLivenessStep('down');
    setMessage(t('watchDown'));
    await wait(3800);
    const down = captureFrame();

    setLivenessStep('done');
    await sendForVerification({ forward, left, down });
  };

  const submitUploads = async () => {
    const forward = uploads.forward || null;
    const left = uploads.left || null;
    const down = uploads.down || null;

    if (!forward || !left || !down) {
      setMessage('Molimo učitajte sve tri slike (Forward, Left, Down).');
      return;
    }
    setLivenessStep('done');
    await sendForVerification({ forward, left, down });
  };

  const stepIndex = (s: LivenessStep) => ({ ready: 0, forward: 1, left: 2, down: 3, done: 3, '': 4 }[s]);

  return (
    <>
      <style>{`
        .overlay-cutout{position:absolute;inset:0;background:rgba(0,0,0,.5);
          -webkit-mask:radial-gradient(ellipse 115px 145px at 50% 44%,transparent 98%,black 102%);
                  mask:radial-gradient(ellipse 115px 145px at 50% 44%,transparent 98%,black 102%);}
        .face-ring{position:absolute;left:50%;top:44%;transform:translate(-50%,-50%);width:230px;height:290px;border-radius:50%/44%;border:2px dashed var(--bs-primary);box-shadow:0 0 0 3px rgba(0,0,0,.55);pointer-events:none;}
        @media(min-width:768px){
          .overlay-cutout{-webkit-mask:radial-gradient(ellipse 190px 240px at 50% 44%,transparent 98%,black 102%);mask:radial-gradient(ellipse 190px 240px at 50% 44%,transparent 98%,black 102%);}
          .face-ring{width:380px;height:480px;}
        }
        .similarity-badge{position:absolute;left:50%;bottom:14%;transform:translateX(-50%);background:rgba(0,0,0,.65);color:#fff;font-size:.85rem;font-weight:700;padding:4px 14px;border-radius:20px;white-space:nowrap;pointer-events:none;}
        .video-active{outline:3px solid #0d6efd;outline-offset:-3px;animation:outlineBlink .9s steps(2, jump-none) infinite;}
        @keyframes outlineBlink{50%{outline-color:rgba(13,110,253,.25);}}
        .guide-bubble{position:absolute;width:64px;height:64px;border-radius:50%;background:#fff;color:#0d6efd;font-weight:800;font-size:30px;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 20px rgba(0,0,0,.25);}
        .guide-left{right:24px;top:42%;animation:floatLeftStrong 1.5s infinite;}
        .guide-down{left:50%;bottom:24px;transform:translateX(-50%);animation:floatDownStrong 1.5s infinite;}
        @keyframes floatLeftStrong{0%{transform:translateX(0);}50%{transform:translateX(-18px);}100%{transform:translateX(0);}}
        @keyframes floatDownStrong{0%{transform:translateY(0);}50%{transform:translateY(18px);}100%{transform:translateY(0);}}
        .step-dots{position:absolute;top:12px;left:12px;display:flex;gap:6px;}
        .step-dot{width:8px;height:8px;border-radius:50%;background:#bbb;opacity:.7;}
        .step-dot.active{background:#0d6efd;opacity:1;}
        .upload-thumb{width:120px;height:160px;object-fit:cover;border-radius:.5rem;border:1px solid rgba(0,0,0,.1);}
      `}</style>

      {mobileNav}

      <Modal show={showResultModal} centered backdrop="static" keyboard={false}>
        <Modal.Body className="d-flex flex-column align-items-center justify-content-center py-5">
          <div style={{
            width: 140, height: 140, borderRadius: '50%',
            background: `conic-gradient(${livenesspassed ? '#198754' : '#dc3545'} 0% ${similarity ?? 0}%, #e9ecef ${similarity ?? 0}% 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: livenesspassed ? '0 4px 20px rgba(25,135,84,.3)' : '0 4px 20px rgba(220,53,69,.3)',
          }}>
            <div style={{
              width: 110, height: 110, borderRadius: '50%',
              background: '#fff', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center'
            }}>
              <span style={{ fontSize: '1.8rem', fontWeight: 800, color: livenesspassed ? '#198754' : '#dc3545', lineHeight: 1 }}>{similarity}%</span>
              <span style={{ fontSize: '.7rem', color: '#6c757d', marginTop: 2 }}>{t('match')}</span>
            </div>
          </div>
          <div className={`mt-3 fw-semibold ${livenesspassed ? 'text-success' : 'text-danger'}`}>
            {livenesspassed ? t('livnessSuccess') : t('livnessNotSuccess')}
          </div>
          {livenesspassed ? (
            <Button
              variant="success"
              className="mt-4 px-5"
              onClick={() => navigate(`/${companyId}/${requestId}/video`)}
            >
              {t('nextStep')} »
            </Button>
          ) : (
            <Button
              variant="outline-primary"
              className="mt-3"
              onClick={() => {
                setShowResultModal(false);
                setSimilarity(null);
                setLivenessStep('ready');
              }}
            >
              {t('tryAgain')}
            </Button>
          )}
        </Modal.Body>
      </Modal>

      <Container className={`${spaceHeight}`}>
        <Row>
          {desktopNav}

          <Col lg={8} xl={7} className="d-flex flex-column px-sm-5">
            <QRCodeModal qrCode={qrCodeSrc} show={showModal} onClose={() => setShowModal(false)} />

            <Row>
              {/* Header: naslov + mode toggle + QR/instructions u jednom redu */}
              <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{t('titleFaceVerification') || 'Liveness Check'}</h5>
                  <div className="d-flex gap-2" style={{ minWidth: 0 }}>
                    <Button
                      size="sm"
                      variant={mode === 'camera' ? 'primary' : 'outline-primary'}
                      onClick={() => setMode('camera')}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Kamera
                    </Button>
                    <Button
                      size="sm"
                      variant={mode === 'upload' ? 'primary' : 'outline-primary'}
                      onClick={() => setMode('upload')}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {t('uploadImages')}
                    </Button>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!isMobile && <QrCodeHelpButton onClick={() => setShowModal(true)} />}
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" type="button"
                    data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight" aria-controls="offcanvasRight">
                    {t('instructions')}
                  </button>
                </div>
              </div>

              {/* Step progress */}
              <StepGuide
                step={stepIndex(livenessStep) === 0 ? 1 : stepIndex(livenessStep)}
                text={`${t('forward')} → ${t('left')} → ${t('down')}`}
                show={livenessStep !== 'done' && livenessStep !== ''}
                arrowAlign="left"
              />


              {/* Camera block */}
              {mode === 'camera' && similarity === null && (
                <div className={`camera-stage rounded-4 overflow-hidden position-relative ${livenessStep !== 'ready' ? 'video-active' : ''}`}
                  style={isMobile ? { height: 420, maxHeight: 'calc(100dvh - 220px)', marginBottom: 24 } : {}}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-100 h-100 object-cover"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  <div className="overlay-cutout" aria-hidden="true"></div>
                  <div className="face-ring" aria-hidden="true"></div>
                  {livenessStep === 'left' && <div className="guide-bubble guide-left">←</div>}
                  {livenessStep === 'down' && <div className="guide-bubble guide-down">↓</div>}

                  {/* Status overlay — instrukcija ili poruka tokom snimanja */}
                  {livenessStep !== 'ready' && (
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 20,
                      padding: '6px 18px', fontSize: '0.9rem', whiteSpace: 'nowrap', pointerEvents: 'none'
                    }}>
                      {livenessStep === 'forward' && t('forward')}
                      {livenessStep === 'left' && t('left')}
                      {livenessStep === 'down' && t('down')}
                      {livenessStep === 'done' && (
                        <span className="d-flex align-items-center gap-2">
                          <Spinner animation="border" size="sm" />
                          {t('analyzingFace')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Start dugme kao overlay unutar kamere */}
                  {livenessStep === 'ready' && (
                    <div style={{
                      position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8
                    }}>
                      <StepGuide
                        step={1}
                        text={t('guideFaceStart')}
                        show={true}
                        arrowAlign="center"
                      />
                      <Button
                        variant="primary"
                        className="px-5 rounded-pill shadow"
                        onClick={startLivenessProcess}
                      >
                        {t('start')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Upload block */}
              {mode === 'upload' && (
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="mb-3">
                      <div className="text-muted small mb-1">{t('uploadThreePictures')}</div>
                    </div>

                    <Row className="g-3">
                      {(['forward', 'left', 'down'] as PoseKey[]).map(pose => (
                        <Col xs={12} sm={6} md={4} key={pose}>
                          <Form.Group controlId={`upload-${pose}`}>
                            <Form.Label className="fw-medium text-capitalize">{t(pose)}</Form.Label>
                            <Form.Control
                              type="file"
                              accept="image/*,.heic,.heif"
                              onChange={(e) => {
                                const input = e.currentTarget as HTMLInputElement;
                                const file = input.files?.[0] ?? null;
                                handleUpload(pose, file);
                              }} />

                            {uploadErrors[pose] && <div className="text-danger small mt-1">{uploadErrors[pose]}</div>}
                            {uploads[pose] && (
                              <div className="mt-2">
                                <img
                                  className="upload-thumb"
                                  src={`data:image/png;base64,${uploads[pose]}`}
                                  alt={`${pose} preview`} />
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>

                    <div className='mt-3'>
                      <StepGuide
                        step={1}
                        text={t('guideFaceUpload')}
                        show={!uploads.forward || !uploads.left || !uploads.down}
                        arrowAlign='left'
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <Button
                        variant="success"
                        className="px-4"
                        onClick={submitUploads}
                        disabled={isProcessing || !uploads.forward || !uploads.left || !uploads.down}
                      >
                        {t('sendToCheck')}
                      </Button>
                      {isProcessing && (
                        <div className="d-flex align-items-center gap-2 text-primary">
                          <Spinner animation="border" size="sm" />
                          <span>Processing</span>
                        </div>
                      )}
                      {!!message && (
                        <div className="small px-3 py-2 rounded-3 ms-2"
                          style={{ background: '#2b5eec', color: '#f6f7f8', fontWeight: 500 }}>
                          {t(message)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Row>
          </Col>

          <div className="offcanvas offcanvas-end" id="offcanvasRight"
            aria-labelledby="offcanvasRightLabel">
            <div className="offcanvas-header">
              <h5 id="offcanvasRightLabel">{t('instructions')}</h5>
              <Button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas"
                aria-label="Close"></Button>
            </div>
            <div className="offcanvas-body">
              <h5 className="mb-4">{t('instructionTitle')}</h5>

              <ol className="mb-3 ps-3">
                <li>{t('faceFirstInstruction')}</li>
                <li>{t('faceSecondInstruction')}</li>
                <li>{t('faceThirdInstruction')}</li>
                <li>{t('faceFourthInstruction')}</li>
              </ol>

              {/* NOVO: kratka napomena o strelicama */}
              <div className="alert alert-info py-2">
                <i className="bi bi-arrow-left-right me-2"></i>
                {t('faceFollowArrowsNote')}
              </div>

              <p className="mb-2">{t('faceExample')}</p>
              <img src="/images/face_id.png" className="img-fluid mt-2" alt="Beispiel für Gesichtserfassung" />
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default FaceIdentification;
